import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import styles from "./index.css?inline";
// Regular import for HMR in development
import "./index.css";
import { installVisitorId } from "./lib/visitorId.js";
import { isAutomatedBrowser } from "./lib/automatedBrowser.js";

// Guest identity for the store API, installed the moment the bundle loads so
// it is in place before the first request (the boot-time session sync).
// On a Salesive-hosted storefront the page's inline script already does this
// and installVisitorId() steps aside; on a merchant's own site embedding the
// widget, this is the only thing that keeps a shopper's guest account, chat
// and sign-in stable across reloads. See src/lib/visitorId.js.
const visitorIdMode = installVisitorId();

// internal Debugger
let isDebugMode = false;

const logger = {
    log: (...args) => {
        if (isDebugMode) {
            console.log("[SalesiveWidget]", ...args);
        }
    },
    warn: (...args) => {
        console.warn("[SalesiveWidget]", ...args);
    },
    error: (...args) => {
        console.error("[SalesiveWidget]", ...args);
    },
};

/**
 * SalesiveWidget - Embeddable Live Sales/Support Widget
 *
 * This module creates a self-contained widget that can be embedded on any website
 * via a single script tag. It uses Shadow DOM for style isolation and provides
 * a global API for programmatic control.
 *
 * Usage:
 *   <script src="widget.js"></script>
 *   <script>
 *     SalesiveWidget.init({ useShadow: true, autoInjectCss: true });
 *   </script>
 *
 * WhatsApp mode: pass `whatsapp: { number: "2348012345678" }` and the launcher
 * becomes a WhatsApp button linking to https://wa.me/<digits>. chat.open(),
 * chat.toggle() and chat.prefill() open WhatsApp instead of the on-site chat.
 * The storefront sets it from shop.settings.chatWidgetMode = "whatsapp".
 */

// Widget state
let root = null;
let hostElement = null;
let shadowRoot = null;
let isInitialized = false;
let isVisible = true;
// Disable Shadow DOM in development for proper HMR
const isDev = import.meta.env?.DEV ?? false;

// Persisted appearance.
//
// Why persist at all: the storefront injects `SalesiveWidget.init()` with no colours,
// and the theme's own React app only calls setTheme()/setDarkMode() once IT has
// mounted. Between those two moments the launcher is already on screen — so we cache
// the last-known-good appearance to avoid a flash of default blue on every page load.
//
// The bug that caused: the cache was keyed GLOBALLY. Switch your storefront theme and
// the widget kept serving the OLD theme's colours, and a `darkMode: true` left over
// from a dark theme made the chat panel dark on a brand-new LIGHT theme — because the
// new theme never calls setDarkMode(false), it simply says nothing.
//
// So the cache is now stamped with the config it belongs to (the served theme's
// provider id, injected by the static server). A different theme means a different
// stamp, the cache is ignored, and we start from that theme's own defaults.
const STORAGE_KEY_DARK_MODE = "salesive_widget_dark_mode";
const STORAGE_KEY_THEME = "salesive_widget_theme";
const STORAGE_KEY_CONFIG_STAMP = "salesive_widget_config_stamp";

/**
 * Drop cached appearance when the storefront's theme has changed.
 * Returns true when the cache is still valid for this config.
 */
function reconcileConfigStamp(configKey) {
    try {
        const previous = localStorage.getItem(STORAGE_KEY_CONFIG_STAMP);

        // No stamp given (older static-server, or a host embedding the widget
        // directly): behave as before rather than throwing the cache away on every
        // load.
        if (!configKey) return true;

        if (previous === configKey) return true;

        // Theme changed — the cached colours and dark mode belong to the previous
        // one and must not leak into this one.
        localStorage.removeItem(STORAGE_KEY_THEME);
        localStorage.removeItem(STORAGE_KEY_DARK_MODE);
        localStorage.setItem(STORAGE_KEY_CONFIG_STAMP, configKey);
        logger.log("Theme changed — cleared cached widget appearance");
        return false;
    } catch (e) {
        // localStorage unavailable: nothing cached, nothing to reconcile.
        return true;
    }
}

/**
 * Get persisted dark mode preference from localStorage
 */
function getPersistedDarkMode() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY_DARK_MODE);
        if (stored !== null) {
            return stored === "true";
        }
    } catch (e) {
        // localStorage not available
    }
    return null;
}

/**
 * Persist dark mode preference to localStorage
 */
function persistDarkMode(enabled) {
    try {
        localStorage.setItem(STORAGE_KEY_DARK_MODE, String(enabled));
    } catch (e) {
        // localStorage not available
    }
}

/**
 * Get persisted theme from localStorage
 */
function getPersistedTheme() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY_THEME);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed && typeof parsed === "object") {
                return parsed; // { primary, secondary, isSecondaryExplicit }
            }
        }
    } catch (e) {
        // localStorage not available or invalid JSON
    }
    return null;
}

/**
 * Persist theme to localStorage
 */
function persistTheme(theme, secondaryExplicit) {
    try {
        localStorage.setItem(
            STORAGE_KEY_THEME,
            JSON.stringify({
                primary: theme.primary,
                secondary: theme.secondary,
                dark: theme.dark,
                isSecondaryExplicit: secondaryExplicit,
            }),
        );
    } catch (e) {
        // localStorage not available
    }
}

const DEFAULT_THEME_RGB = "13 101 217";
const DEFAULT_DARK_RGB = "17 24 39";

let currentOptions = {
    hostElementId: "salesive-widget-host",
    cssUrl: null,
    autoInjectCss: true,
    useShadow: !isDev, // Shadow DOM disabled in dev for HMR
    routes: null, // Array of route prefixes to show widget on (null = all routes)
    darkMode: false, // Will be set from persistence or config
    theme: {
        primary: null,
        secondary: null,
        dark: null,
    },
};

let isSecondaryExplicit = false;

let appliedThemeRgb = {
    primary: DEFAULT_THEME_RGB,
    secondary: DEFAULT_THEME_RGB,
    dark: DEFAULT_DARK_RGB,
};

/**
 * Load persisted settings into currentOptions
 */
function loadPersistedSettings(configKey) {
    // If the storefront's theme changed, the cache below belongs to the old one.
    reconcileConfigStamp(configKey);

    // Dark mode.
    //
    // This is storefront CONFIG, not a user preference — there is no toggle anywhere
    // in the widget. Caching it was what left the chat panel dark on a light theme:
    // a previous theme had set dark, the new one simply never calls setDarkMode, and
    // the stale `true` won. The stamp above clears it on a theme change; this only
    // restores it for the SAME theme, to avoid a flash of light before the theme's
    // own script re-applies it.
    const persistedDarkMode = getPersistedDarkMode();
    if (persistedDarkMode !== null) {
        currentOptions.darkMode = persistedDarkMode;
    }

    // Theme.
    //
    // The cache only ever FILLS GAPS. `window.SalesiveWidgetConfig` (injected by the
    // server, applied at module load) and the init options are authoritative — a
    // cached colour must never overwrite what this page actually asked for, or we are
    // straight back to serving the previous theme's branding.
    const persistedTheme = getPersistedTheme();
    if (persistedTheme) {
        if (
            currentOptions.theme.primary == null &&
            typeof persistedTheme.primary === "string"
        ) {
            currentOptions.theme.primary = persistedTheme.primary;
        }
        if (
            currentOptions.theme.secondary == null &&
            typeof persistedTheme.secondary === "string"
        ) {
            currentOptions.theme.secondary = persistedTheme.secondary;
            isSecondaryExplicit = !!persistedTheme.isSecondaryExplicit;
        }
        if (
            currentOptions.theme.dark == null &&
            typeof persistedTheme.dark === "string"
        ) {
            currentOptions.theme.dark = persistedTheme.dark;
        }
    }
}

// Initial load
// NOTE: deliberately NOT called at module load. The config stamp arrives with
// init(), and reading the cache before we know which theme it belongs to is exactly
// how the old theme's colours leaked into the new one.

// Pick up pre-init config from window.SalesiveWidgetConfig (set before script load)
if (typeof window !== "undefined" && window.SalesiveWidgetConfig) {
    const preConfig = window.SalesiveWidgetConfig;
    if (preConfig.theme) {
        if (typeof preConfig.theme.primary === "string") {
            currentOptions.theme.primary = preConfig.theme.primary;
        }
        if (Object.prototype.hasOwnProperty.call(preConfig.theme, "secondary")) {
            if (typeof preConfig.theme.secondary === "string") {
                currentOptions.theme.secondary = preConfig.theme.secondary;
                isSecondaryExplicit = true;
            } else if (preConfig.theme.secondary == null) {
                currentOptions.theme.secondary = null;
                isSecondaryExplicit = false;
            }
        }
        if (typeof preConfig.theme.dark === "string") {
            currentOptions.theme.dark = preConfig.theme.dark;
        }
    }
    logger.log("Pre-init config applied from window.SalesiveWidgetConfig");
}

function cloneTheme(theme) {
    return {
        primary: theme?.primary ?? null,
        secondary: theme?.secondary ?? null,
        dark: theme?.dark ?? null,
    };
}

/**
 * Normalize any CSS color string to a space-separated RGB triplet ("r g b").
 * Returns null if invalid or if the DOM isn't available.
 */
function normalizeCssColorToRgbTriplet(color) {
    if (typeof color !== "string" || color.trim() === "") {
        return null;
    }
    if (typeof document === "undefined") {
        return null;
    }

    const mountTarget = document.body || document.documentElement;
    if (!mountTarget) {
        return null;
    }

    const probe = document.createElement("span");
    probe.style.position = "absolute";
    probe.style.left = "-9999px";
    probe.style.top = "-9999px";
    probe.style.opacity = "0";
    probe.style.pointerEvents = "none";

    probe.style.color = "";
    probe.style.color = color;

    // If the browser rejects the value, it stays empty.
    if (!probe.style.color) {
        return null;
    }

    mountTarget.appendChild(probe);
    const computed = getComputedStyle(probe).color;
    probe.remove();

    const match = computed.match(
        /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i,
    );
    if (!match) {
        return null;
    }

    const r = Number(match[1]);
    const g = Number(match[2]);
    const b = Number(match[3]);
    if (
        !Number.isFinite(r) ||
        !Number.isFinite(g) ||
        !Number.isFinite(b) ||
        r < 0 ||
        r > 255 ||
        g < 0 ||
        g > 255 ||
        b < 0 ||
        b > 255
    ) {
        return null;
    }

    return `${r} ${g} ${b}`;
}

function applyThemeToHost() {
    if (!hostElement) return;

    let nextPrimaryRgb = DEFAULT_THEME_RGB;
    if (currentOptions.theme.primary != null) {
        const normalizedPrimary = normalizeCssColorToRgbTriplet(
            currentOptions.theme.primary,
        );
        if (normalizedPrimary) {
            nextPrimaryRgb = normalizedPrimary;
        } else {
            logger.warn(
                "Invalid theme.primary color; keeping previous value:",
                currentOptions.theme.primary,
            );
            nextPrimaryRgb = appliedThemeRgb.primary;
        }
    }

    let nextSecondaryRgb = nextPrimaryRgb;
    if (isSecondaryExplicit && currentOptions.theme.secondary != null) {
        const normalizedSecondary = normalizeCssColorToRgbTriplet(
            currentOptions.theme.secondary,
        );
        if (normalizedSecondary) {
            nextSecondaryRgb = normalizedSecondary;
        } else {
            logger.warn(
                "Invalid theme.secondary color; keeping previous value:",
                currentOptions.theme.secondary,
            );
            nextSecondaryRgb = appliedThemeRgb.secondary;
        }
    }

    let nextDarkRgb = DEFAULT_DARK_RGB;
    if (currentOptions.theme.dark != null) {
        const normalizedDark = normalizeCssColorToRgbTriplet(
            currentOptions.theme.dark,
        );
        if (normalizedDark) {
            nextDarkRgb = normalizedDark;
        } else {
            logger.warn(
                "Invalid theme.dark color; keeping previous value:",
                currentOptions.theme.dark,
            );
            nextDarkRgb = appliedThemeRgb.dark;
        }
    }

    appliedThemeRgb = {
        primary: nextPrimaryRgb,
        secondary: nextSecondaryRgb,
        dark: nextDarkRgb,
    };

    hostElement.style.setProperty("--salesive-primary", nextPrimaryRgb);
    hostElement.style.setProperty("--salesive-secondary", nextSecondaryRgb);
    hostElement.style.setProperty("--salesive-dark", nextDarkRgb);

    // `.support-widget-root` sets `all: initial`, which blocks inheritance — so the
    // variables must land on THAT element, not just the host.
    //
    // This used to hunt for the element with querySelector and, when it wasn't
    // mounted yet, simply skip. React mounts asynchronously, so the code chased it
    // with rAF + setTimeout(0) + setTimeout(500) and hoped. On a slow device the
    // root mounts after all three have fired, the variables never land, and
    // `rgb(var(--salesive-primary))` resolves to nothing — which is why the launcher
    // bubble was sometimes TRANSPARENT, then correct once you opened it (a later
    // re-render finally caught a re-apply).
    //
    // A CSS rule has no such race: it applies the moment the element exists, whenever
    // that is. No timers, no guessing.
    applyThemeRule(nextPrimaryRgb, nextSecondaryRgb, nextDarkRgb);
}

// The <style> element holding the theme rule, and the node it currently lives in.
// Kept around so updates rewrite the rule rather than stacking duplicates.
let themeStyleElement = null;
let themeStyleTarget = null;

function applyThemeRule(primaryRgb, secondaryRgb, darkRgb) {
    const target = shadowRoot || document.head;
    if (!target) return;

    // The style element has to MOVE when the target changes, not just be reused
    // wherever it first landed.
    //
    // init() applies the theme before it builds the mount point, so the first call
    // happens while `shadowRoot` is still null and the rule lands in document.head.
    // A stylesheet in the document cannot cross a shadow boundary, so once the
    // widget mounts into shadow DOM that rule reaches nothing: `.support-widget-root`
    // never gets `--salesive-primary`, and every surface falls back to the default
    // blue — including the colour the theme later sets with setTheme(), because the
    // element was still `isConnected` (in the head) and got reused in place.
    const targetChanged = themeStyleTarget !== target;

    if (!themeStyleElement || !themeStyleElement.isConnected || targetChanged) {
        if (themeStyleElement?.isConnected) themeStyleElement.remove();

        themeStyleElement = document.createElement("style");
        themeStyleElement.setAttribute("data-salesive-theme-vars", "");
        target.appendChild(themeStyleElement);
        themeStyleTarget = target;
    }

    themeStyleElement.textContent = `.support-widget-root {
        --salesive-primary: ${primaryRgb};
        --salesive-secondary: ${secondaryRgb};
        --salesive-dark: ${darkRgb};
    }`;
}

/**
 * Injects CSS into the target (shadow root or document head)
 */
function injectStyles(target, cssText) {
    const styleElement = document.createElement("style");
    styleElement.textContent = cssText;

    if (target === document.head) {
        // For non-shadow mode, add a data attribute to identify our styles
        styleElement.setAttribute("data-salesive-widget", "true");
        target.appendChild(styleElement);
    } else {
        // For shadow DOM, prepend styles
        target.insertBefore(styleElement, target.firstChild);
    }

    return styleElement;
}

/**
 * Loads external CSS file
 */
function loadExternalCss(url, target) {
    return new Promise((resolve, reject) => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = url;
        link.onload = resolve;
        link.onerror = reject;

        if (target === document.head) {
            link.setAttribute("data-salesive-widget", "true");
        }

        target.appendChild(link);
    });
}

/**
 * Creates or gets the host element for the widget
 */
function getOrCreateHostElement(elementId) {
    let element = document.getElementById(elementId);

    if (!element) {
        element = document.createElement("div");
        element.id = elementId;
        // Position the host element
        element.style.cssText = `
      position: fixed;
      bottom: 0;
      right: 0;
      z-index: 2147483647;
      pointer-events: none;
    `;
        document.body.appendChild(element);
    }

    return element;
}

/**
 * Creates the mount point (either shadow root or regular div)
 */
function createMountPoint(host, useShadow) {
    if (useShadow) {
        // Create shadow DOM for style isolation
        const shadow = host.attachShadow({ mode: "open" });

        // Create a container inside shadow DOM
        const container = document.createElement("div");
        container.id = "salesive-widget-container";
        container.style.cssText = "pointer-events: auto;";
        shadow.appendChild(container);

        return { shadow, container };
    } else {
        // Non-shadow mode: create a regular container
        const container = document.createElement("div");
        container.id = "salesive-widget-container";
        container.style.cssText = "pointer-events: auto;";
        host.appendChild(container);

        return { shadow: null, container };
    }
}

const widgetController = {
    subscription: {
        openModal: () => console.warn("Widget not initialized"),
        getSubscriptionCount: () => 0,
    },
    pwa: {
        openModal: () => console.warn("Widget not initialized"),
        isInstallable: () => false,
    },
    reviews: {
        open: () => console.warn("Widget not initialized"),
        close: () => console.warn("Widget not initialized"),
    },
    chat: {
        open: () => console.warn("Widget not initialized"),
        close: () => console.warn("Widget not initialized"),
        toggle: () => console.warn("Widget not initialized"),
        prefill: () => console.warn("Widget not initialized"),
        isOpened: false,
    },
};

/**
 * Initialize the widget with options
 */
function init(options = {}) {
    logger.log("Visitor id:", visitorIdMode);
    // A crawler or headless browser is not a visitor: starting would create a
    // guest session and a chat socket, and Ola would greet it.
    if (isAutomatedBrowser()) {
        logger.log("Automated browser detected, widget not started");
        return;
    }
    if (isInitialized) {
        console.warn(
            "[SalesiveWidget] Already initialized. Call unmount() first to reinitialize.",
        );
        return;
    }

    // Inject or update viewport meta for proper mobile chat behavior
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
        viewportMeta = document.createElement("meta");
        viewportMeta.name = "viewport";
        document.head.appendChild(viewportMeta);
    }
    viewportMeta.content =
        "width=device-width, initial-scale=1.0, user-scalable=no, maximum-scale=1.0, minimum-scale=1.0, viewport-fit=cover, interactive-widget=resizes-content";

    // Load the cached appearance FIRST, scoped to the theme this page is serving.
    // A different theme invalidates the cache instead of inheriting from it.
    loadPersistedSettings(options?.configKey);

    // Merge options (shallow merge + nested theme merge)
    const nextTheme = options.theme
        ? { ...currentOptions.theme, ...options.theme }
        : currentOptions.theme;
    currentOptions = { ...currentOptions, ...options, theme: nextTheme };
    if (options?.theme && Object.prototype.hasOwnProperty.call(options.theme, "secondary")) {
        isSecondaryExplicit = options.theme.secondary != null;
    }
    // Persist theme if provided via init options
    if (options?.theme) {
        persistTheme(currentOptions.theme, isSecondaryExplicit);
    }

    // Create host element
    hostElement = getOrCreateHostElement(currentOptions.hostElementId);
    applyThemeToHost();

    // Create mount point
    const { shadow, container } = createMountPoint(
        hostElement,
        currentOptions.useShadow,
    );
    shadowRoot = shadow;

    // Re-apply now that the shadow root exists: the call above could only reach
    // document.head, which the shadow tree cannot see. This is what puts the cached
    // brand colour on the launcher for first paint, instead of a flash of default
    // blue until the theme gets round to calling setTheme().
    applyThemeToHost();

    // Determine where to inject styles
    const styleTarget = shadowRoot || document.head;

    // Inject styles
    if (currentOptions.autoInjectCss) {
        // Use inline styles (bundled with the widget)
        injectStyles(styleTarget, styles);
    }

    // Load external CSS if provided
    if (currentOptions.cssUrl) {
        loadExternalCss(currentOptions.cssUrl, styleTarget).catch((err) => {
            logger.error("Failed to load external CSS:", err);
        });
    }

    // Create React root and render
    root = ReactDOM.createRoot(container);
    root.render(
        React.createElement(App, {
            routes: currentOptions.routes,
            darkMode: currentOptions.darkMode,
            controller: widgetController,
            whatsapp: currentOptions.whatsapp,
        }),
    );

    isInitialized = true;

    // Apply theme immediately
    applyThemeToHost();

    // No timer chase any more: applyThemeToHost writes a CSS RULE for
    // .support-widget-root, which applies whenever React gets round to mounting it.

    logger.log("Initialized successfully");
}

/**
 * Mount the widget to a specific selector (alternative to init)
 */
function mount(selector) {
    if (isAutomatedBrowser()) {
        logger.log("Automated browser detected, widget not started");
        return;
    }
    if (isInitialized) {
        console.warn("[SalesiveWidget] Already mounted. Call unmount() first.");
        return;
    }

    let targetElement;

    if (selector) {
        targetElement = document.querySelector(selector);
        if (!targetElement) {
            console.error(`[SalesiveWidget] Element not found: ${selector}`);
            return;
        }
    } else {
        // Create default host element
        targetElement = getOrCreateHostElement(currentOptions.hostElementId);
    }

    hostElement = targetElement;
    applyThemeToHost();

    // Create mount point
    const { shadow, container } = createMountPoint(
        hostElement,
        currentOptions.useShadow,
    );
    shadowRoot = shadow;

    // Same as init(): the theme rule can only reach the shadow tree once it exists.
    applyThemeToHost();

    // Inject styles
    const styleTarget = shadowRoot || document.head;
    injectStyles(styleTarget, styles);

    // Create React root and render
    root = ReactDOM.createRoot(container);
    root.render(
        React.createElement(App, {
            routes: currentOptions.routes,
            darkMode: currentOptions.darkMode,
            controller: widgetController,
            whatsapp: currentOptions.whatsapp,
        }),
    );

    isInitialized = true;
    requestAnimationFrame(() => applyThemeToHost());
    logger.log("Mounted successfully");
}

/**
 * Helper to re-render app with current options
 */
function rerender() {
    if (root) {
        root.render(
            React.createElement(App, {
                routes: currentOptions.routes,
                darkMode: currentOptions.darkMode,
                controller: widgetController,
                whatsapp: currentOptions.whatsapp,
            }),
        );
    }
}

/**
 * Update widget routes dynamically
 * @param {string[]|null} routes - Array of route prefixes or null for all routes
 */
function setRoutes(routes) {
    if (!isInitialized) {
        console.warn("[SalesiveWidget] Not initialized. Call init() first.");
        return;
    }

    currentOptions.routes = routes;
    rerender();
    logger.log("Routes updated:", routes);
}

/**
 * Toggle or set dark mode
 * @param {boolean} [enabled] - If provided, sets dark mode to this value. Otherwise toggles.
 */
function setDarkMode(enabled) {
    if (!isInitialized) {
        console.warn("[SalesiveWidget] Not initialized. Call init() first.");
        return;
    }

    currentOptions.darkMode =
        typeof enabled === "boolean" ? enabled : !currentOptions.darkMode;
    persistDarkMode(currentOptions.darkMode);
    rerender();
    logger.log("Dark mode:", currentOptions.darkMode);
}

/**
 * Update widget theme colors at runtime.
 * Accepts any CSS color string (hex/rgb/hsl/name).
 * - If called before init, values are stored and applied on init.
 * - `secondary` defaults to `primary` unless explicitly set.
 */
function setTheme(theme = {}) {
    const nextTheme = { ...currentOptions.theme };

    if (Object.prototype.hasOwnProperty.call(theme, "primary")) {
        nextTheme.primary =
            typeof theme.primary === "string" ? theme.primary : null;
        if (theme.primary != null && typeof theme.primary !== "string") {
            logger.warn("theme.primary must be a CSS color string.");
        }
    }

    if (Object.prototype.hasOwnProperty.call(theme, "secondary")) {
        if (theme.secondary == null) {
            nextTheme.secondary = null;
            isSecondaryExplicit = false;
        } else if (typeof theme.secondary === "string") {
            nextTheme.secondary = theme.secondary;
            isSecondaryExplicit = true;
        } else {
            logger.warn("theme.secondary must be a CSS color string.");
        }
    }

    if (Object.prototype.hasOwnProperty.call(theme, "dark")) {
        nextTheme.dark =
            typeof theme.dark === "string" ? theme.dark : null;
        if (theme.dark != null && typeof theme.dark !== "string") {
            logger.warn("theme.dark must be a CSS color string.");
        }
    }

    currentOptions.theme = nextTheme;
    persistTheme(nextTheme, isSecondaryExplicit);

    if (!isInitialized) {
        return;
    }

    applyThemeToHost();
}

function getTheme() {
    return cloneTheme(currentOptions.theme);
}

/**
 * Hide the widget (keeps it mounted, just visually hidden)
 */
function hide() {
    if (!isInitialized) {
        console.warn("[SalesiveWidget] Not initialized. Call init() first.");
        return;
    }

    if (hostElement) {
        hostElement.style.display = "none";
        isVisible = false;
        logger.log("Hidden");
    }
}

/**
 * Show the widget (if it was hidden)
 */
function show() {
    if (!isInitialized) {
        console.warn("[SalesiveWidget] Not initialized. Call init() first.");
        return;
    }

    if (hostElement) {
        hostElement.style.display = "";
        isVisible = true;
        logger.log("Shown");
    }
}

/**
 * Toggle widget visibility
 * @returns {boolean} The new visibility state
 */
function toggle() {
    if (isVisible) {
        hide();
    } else {
        show();
    }
    return isVisible;
}

/**
 * Unmount the widget and clean up
 */
function unmount() {
    if (!isInitialized) {
        console.warn("[SalesiveWidget] Not initialized.");
        return;
    }

    // Unmount React
    if (root) {
        root.unmount();
        root = null;
    }

    // Clean up shadow DOM or container
    if (shadowRoot) {
        // Shadow DOM is automatically cleaned up when host is removed
        shadowRoot = null;
    }

    // The theme rule lived inside that shadow tree. Drop the reference so a later
    // init() builds a fresh one against the new root rather than a detached node.
    if (themeStyleElement?.isConnected) {
        themeStyleElement.remove();
    }
    themeStyleElement = null;
    themeStyleTarget = null;

    // Remove host element if we created it
    if (hostElement && hostElement.id === currentOptions.hostElementId) {
        hostElement.remove();
    }
    hostElement = null;

    // Remove any styles we added to document head (non-shadow mode)
    document
        .querySelectorAll(
            "style[data-salesive-widget], link[data-salesive-widget]",
        )
        .forEach((el) => {
            el.remove();
        });

    isInitialized = false;
    isVisible = true; // Reset visibility state
    logger.log("Unmounted successfully");
}

/**
 * Set debug mode
 * @param {boolean} enabled - Whether to enable debug logging
 */
function setDebugMode(enabled) {
    isDebugMode = !!enabled;
    logger.log("Debug mode:", isDebugMode);
}

// Create the global API
const SalesiveWidget = {
    init,
    mount,
    unmount,
    setDebugMode,
    setTheme,
    getTheme,

    // Deprecated global methods
    hide: () => {
        logger.warn(
            "SalesiveWidget.hide() is deprecated. Please use SalesiveWidget.chat.hide() instead.",
        );
        hide();
    },
    show: () => {
        logger.warn(
            "SalesiveWidget.show() is deprecated. Please use SalesiveWidget.chat.show() instead.",
        );
        show();
    },
    toggle: () => {
        logger.warn(
            "SalesiveWidget.toggle() is deprecated. Please use SalesiveWidget.chat.toggleVisibility() instead.",
        );
        return toggle();
    },
    setRoutes: (routes) => {
        logger.warn(
            "SalesiveWidget.setRoutes() is deprecated. Please use SalesiveWidget.chat.setRoutes() instead.",
        );
        setRoutes(routes);
    },
    setDarkMode: (enabled) => {
        logger.warn(
            "SalesiveWidget.setDarkMode() is deprecated. Please use SalesiveWidget.chat.setDarkMode() instead.",
        );
        setDarkMode(enabled);
    },
    get visible() {
        logger.warn(
            "SalesiveWidget.visible is deprecated. Please use SalesiveWidget.chat.isVisible instead.",
        );
        return isVisible;
    },

    // Expose version for debugging
    version: "1.0.0",

    // Subscription API
    subscription: {
        open: () => widgetController.subscription.openModal(),
        getSubscriptionCount: () =>
            widgetController.subscription.getSubscriptionCount(),
    },
    // PWA API
    pwa: {
        open: () => widgetController.pwa.openModal(),
        isInstallable: () => widgetController.pwa.isInstallable(),
    },
    // Reviews API
    reviews: {
        open: () => widgetController.reviews.open(),
        close: () => widgetController.reviews.close(),
    },
    // Chat API
    chat: {
        // Window control
        open: () => widgetController.chat.open(),
        close: () => widgetController.chat.close(),
        toggle: () => widgetController.chat.toggle(),
        get isOpened() {
            return widgetController.chat.isOpened;
        },

        // Prefill
        prefill: (opts) => widgetController.chat.prefill(opts),

        // Visibility & Configuration
        hide,
        show,
        toggleVisibility: toggle,
        get isVisible() {
            return isVisible;
        },
        setRoutes,
        setDarkMode,

        // Voice call API. Everything here is safe to call blind from theme
        // code: reads return inert defaults before init, actions warn, and
        // start()/isEnabled() resolve rather than reject. The enabled flag and
        // the actual call are enforced SERVER-side regardless — this surface
        // is UX, not authorization.
        call: {
            get status() {
                return widgetController.chat.call?.status ?? "idle";
            },
            get muted() {
                return widgetController.chat.call?.muted ?? false;
            },
            get speakerOn() {
                return widgetController.chat.call?.speakerOn ?? true;
            },
            getState: () =>
                widgetController.chat.call?.getState?.() ?? {
                    status: "idle",
                    muted: false,
                    speakerOn: true,
                },
            // Resolves true/false, or null when it cannot be determined yet
            // (no network, session still being created).
            isEnabled: async () => {
                if (widgetController.chat.call?.isEnabled) {
                    return widgetController.chat.call.isEnabled();
                }
                logger.warn("Widget not initialized yet. Call init() first.");
                return null;
            },
            // Opens the chat and starts a call to the store, if the store has
            // voice calls enabled. Resolves { started, reason } where reason is
            // one of: disabled | busy | unavailable | timeout.
            start: async () => {
                if (widgetController.chat.call?.start) {
                    return widgetController.chat.call.start();
                }
                logger.warn("Widget not initialized yet. Call init() first.");
                return { started: false, reason: "not-initialized" };
            },
            hangup: () => {
                if (widgetController.chat.call) {
                    widgetController.chat.call.hangup();
                } else {
                    logger.warn("Widget not initialized yet. Call init() first.");
                }
            },
            toggleMute: () => {
                if (widgetController.chat.call?.toggleMute) {
                    widgetController.chat.call.toggleMute();
                } else {
                    logger.warn("Widget not initialized yet. Call init() first.");
                }
            },
            toggleSpeaker: () => {
                if (widgetController.chat.call?.toggleSpeaker) {
                    widgetController.chat.call.toggleSpeaker();
                } else {
                    logger.warn("Widget not initialized yet. Call init() first.");
                }
            },
            // Subscribe to call status transitions (idle → outgoing →
            // connecting → connected → idle). Returns an unsubscribe function.
            onStatusChange: (handler) => {
                if (widgetController.chat.call?.onStatusChange) {
                    return widgetController.chat.call.onStatusChange(handler);
                }
                logger.warn("Widget not initialized yet. Call init() first.");
                return () => {};
            },
        },

        // Socket Access
        on: (event, handler) => {
            if (widgetController.chat.on) {
                widgetController.chat.on(event, handler);
            } else {
                logger.warn("Socket not initialized yet. Call init() first.");
            }
        },
        emit: (event, ...args) => {
            if (widgetController.chat.emit) {
                widgetController.chat.emit(event, ...args);
            } else {
                logger.warn("Socket not initialized yet. Call init() first.");
            }
        },
    },
};

// Expose to window
if (typeof window !== "undefined") {
    window.SalesiveWidget = SalesiveWidget;
}

// Auto-initialize if data attribute is present on script tag
if (typeof document !== "undefined") {
    document.addEventListener("DOMContentLoaded", () => {
        const scriptTag =
            document.currentScript ||
            document.querySelector("script[data-salesive-widget-auto]");
        if (scriptTag && scriptTag.hasAttribute("data-salesive-widget-auto")) {
            init();
        }
    });
}

export default SalesiveWidget;
